<template lang="pug">
  management-page(title="Campaign Summary"
              :creatable="false"
              :searchable="false"
              :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16" justify="end")
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")

      aiq-row(:gutter="16" justify="start")
        aiq-col(:span="24")
          .dropdown-filter
            aiq-select(placeholder="Filter by channels"
                                             v-model="chosenChannelFilterOptions"
                                             :value="chosenChannelFilterOptions"
                                             multiple
                                             :reserve-keyword="false"
                                             @change="onChannelFilterChange($event)"
                                             value-key='name'
                                             size="small")
              aiq-option(v-for="option in channelsFilterOptions"
                                        :key="option.id"
                                        :label="channelOptionLabel(option.name)"
                                        :value="option")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(
                           v-if="configs.campaign_status_counts"
                           :dateRange="dateRangeUtc"
                           :chartConfig="campaignStatusChartConfig"
                           yLabel="Count"
                           grouped
                           :showInternalBucket="false"
                           :filters="selectedFilters"
                           :formatYTick="intCountFormatter"
                           visualization='aiq-bar-chart'
                           :downloadWithTimeTicks="true"
                           :style="{ height: '100%' }"
                           :useDebounceForLoadMetrics="true"
                           :shouldHandlePartialDates="true"
                           :reportFileName="returnReportFileName('Campaign status counts')"
                            )
      .common-campaign-table-toolbar
        span {{campaignMsgTableTitle}}
        .flex-spacer
        aiq-button.export(@click="exportMsgsTable")
          i.iq-ico-export
      aiq-table.scrollbar(:data="campaignMsgItems"
                ref="campaignMsgsTable"
                style="width: 100%; height: auto !important;"
                :span-method="arraySpanMethod"
                v-loading="loading")
        aiq-table-column(label="Campaign", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
              span {{ scope.row.campaign_name }} &nbsp;
        aiq-table-column(label="Message template", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
              span {{ getTemplateName(scope.row) }} &nbsp;
        aiq-table-column(label="Interaction type", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
              span {{ getCampaignType(scope.row) }} &nbsp;
        aiq-table-column(label="Scheduled", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
            span {{ scope.row.started_count }} &nbsp;
        aiq-table-column(label="Sent", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
            span {{ scope.row.executed_count }} &nbsp;
        aiq-table-column(label="Stopped", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
            span {{ scope.row.stopped_count }} &nbsp;
        aiq-table-column(label="Deleted", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
            span {{ scope.row.deleted_count }} &nbsp;
        aiq-table-column(label="Failed", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
            span {{ scope.row.failed_count }} &nbsp;
        aiq-table-column(label="Total", :min-width="15", label-class-name="is-bordered normal-word-break")
          template(v-slot:default="scope")
              span {{ scope.row.total_count }} &nbsp;
</template>


<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import campaignStatusChartConfig from '../../configs/campaignStatus';
import campaignMessagesChartConfig from '../../configs/CampaignMessagesChart';
import {
  createPickerDateRangeOption,
  datePickerDefaultOptions, defaultDateRange, dstDateRangeUtc,
  lastAvailableMonthAndQuarterRange,
} from '@/pages/Metrics/configs/dateRange';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import upperFirst from 'lodash/upperFirst';
import { downloadAsFile, formatDateWithTimezone, getMetricsTimeZoneAbbr, useTimeZone } from '@/libs';
import dayjs from '@/localPlugins/dayjs';
import { intTickFormatter } from '@/pages/Metrics/helpers/formatters';
import { countBy } from 'lodash';
import { loadFilterOptions } from '@/pages/Metrics/helpers/filterHelper';
import PlatformFiltersMixin from '@/pages/Metrics/Platform/children/selectableFilters/SelectableFilters';
import isEmpty from 'lodash/isEmpty';

export default {
  pageResource: '/metrics',
  components: {
    ManagementPage,
    OverviewChart,
  },
  mixins: [PlatformFiltersMixin],
  data() {
    return {
      campaignStatusChartConfig,
      campaignMessagesChartConfig,
      campaignItems: [],
      campaignMsgItems: [],
      rowSpans: [],
      dateRange: defaultDateRange(),
      intCountFormatter: intTickFormatter,
      loading: true,
      channelsEndpoint: 'all_channels',
      chosenChannelFilterOptions: [],
      channelsFilterOptions: [],
      selectedFilters: [],
    };
  },
  watch: {
    dateRange: debounce(async function onDateRangeChange(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.loadCampaignMsgsList();
      }
    }, 1),
    selectedFilters() {
      this.loadCampaignMsgsList();
    },
  },
  computed: {
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    campaignMsgTableTitle() {
      return `Message status counts as of ${dayjs(this.dateRange[1]).format('MMM DD, YYYY')}`;
    },
    dateParams() {
      const dateRange = this.dateRangeUtc;
      const periodStart = dateRange[0];
      const periodEnd = dateRange[1];
      if (!(periodStart && periodEnd)) {
        return;
      }
      return {
        period_start: periodStart,
        period_end: periodEnd,
      };
    },
    ...mapGetters({
      configs: 'configs/graphsConfigs',
    }),
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    await this.loadTzAbbr(this.timezone);
    await this.loadCampaignMsgsList();
    await loadFilterOptions(this, this.channelsEndpoint);
  },
  methods: {
    //eslint-disable-next-line
    arraySpanMethod(row, column, rowIndex, columnIndex) {
      if (row.columnIndex === 0) {
        return [this.rowSpans[row.rowIndex], 1];
      }
      return [1, 1];
    },
    channelOptionLabel(name) {
      if (name === 'sms') {
        return 'SMS';
      }
      return upperFirst(name);
    },
    MessagesChartData(row) {
      return {
        dataset: [{
          data_points: [{
            name: 'executed',
            value: row.executed_percent,
          },
          {
            name: 'stopped',
            value: row.stopped_percent,
          },
          {

            name: 'deleted',
            value: row.deleted_percent,
          },
          {

            name: 'pending',
            value: row.pending_percent,
          },
          {
            name: 'failed',
            value: row.failed_percent,
          },
          ],
        }],
      };
    },
    onSortField(path) {
      return (a, b) => {
        return a[path] < b[path] ? 1 : -1;
      };
    },
    getCampaignName(row) {
      return get(row, 'campaign_name', '');
    },
    getTemplateName(row) {
      return get(row, 'message_template', '');
    },
    getCampaignType(row) {
      const campaign_type = get(row, 'campaign_type', '');
      if (campaign_type === 'agent_campaign') {
        return 'Notification';
      }
      return 'Message';
    },
    getCampaignStatus(row) {
      const status = get(row, 'status', '');
      if (status === 'customer_campaign_created') {
        return 'Started';
      } else if (status === 'executed') {
        return 'Sent';
      } else if (status === 'deleted') {
        return 'Deleted';
      } else if (status === 'failed') {
        return 'Failed';
      }
      return status;
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp)
          .format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp)
          .format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    exportTable() {
      const data_points = [];
      for (const c of this.campaignItems) {
        data_points.push(
          {
            'Campaign': this.getCampaignName(c) || '',
            'Status Date UTC': c.created_at || '',
            'Status Date': this.getCampaignDate(c) || '',
            'Sentence Type': c.message_type || '',
            'Template': c.message_template || '',
            'Status': this.getCampaignStatus(c) || '',
            'Lynq ID': c.customer_id || '',
            'Customer Campaign ID': c.customer_campaign_id || '',
            'Campaign ID': c.campaign_id || '',
          },
        );
      }
      downloadAsFile({
        dataType: 'table',
        data: sortBy(data_points, 'Created At UTC', 'asc'),
        filename: this.returnReportFileName('Message_details'),
      });
    },

    exportMsgsTable() {
      const data_points = [];
      for (const c of this.campaignMsgItems) {
        data_points.push(
          {
            'Campaign': c.campaign_name || '',
            'Message Template': c.message_template || '',
            'Sent': c.executed_count || 0,
            'Stopped': c.stopped_count || 0,
            'Scheduled': c.started_count || 0,
            'Deleted': c.deleted_count || 0,
            'Failed': c.failed_count || 0,
            'Total': c.total_count || 0,

          },
        );
      }
      downloadAsFile({
        dataType: 'table',
        data: data_points,
        filename: this.returnReportFileName('Message status counts'),
      });
    },
    async loadTzAbbr(tz) {
      this.tzAbbr = getMetricsTimeZoneAbbr(tz);
    },
    getCampaignDate(row) {
      return `${formatDateWithTimezone(row.created_at, this.timezone, 'MMM DD, YYYY hh:mm A')} ${this.tzAbbr}`;
    },
    async loadCampaignMsgsList() {
      const params = this.dateParams;
      if (!isEmpty(this.selectedFilters)) {
        params.filters = this.selectedFilters;
      }
      const result = await this.$store
        .dispatch('metrics/getMetric', ['campaign_message_counts', this.dateParams]);
      const dataset = get(result, 'dataset', []);
      this.campaignMsgItems = dataset.map(data => data.data_points);
      const items = this.campaignMsgItems;
      const campaignCounts = countBy(items, 'campaign_name');
      const rowSpans = Array(items.length).fill(0);
      const seenSpans = new Map();
      for (let i = 0; i < items.length; i++) {
        if (seenSpans.get(items[i].campaign_name)) {
          continue;
        }
        const campaign_name = items[i].campaign_name;
        rowSpans[i] = campaignCounts[campaign_name];
        seenSpans.set(campaign_name, rowSpans[i]);
      }
      this.rowSpans = rowSpans;
      this.loading = false;
    },
  },
};
</script>


<style scoped lang="scss">
@import "../../../../styles/aiq-variables.scss";

.content-inner.summary {
  overflow-y: auto;
}
.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.time-range-filter {
  position: relative;
  margin-bottom: 24px;
}

.dropdown-filter {
  margin-right: 12px;
  width: 25em;
  margin-bottom: 24px;

  :deep(.el-select) {
    width: 75%;
  }
}

.common-campaign-table-toolbar {
  display: flex;
  align-items: flex-end;
  margin-bottom: 24px;
  margin-top: 32px;
  .flex-spacer {
    flex-grow: 1;
  }
  .el-button.export {
    height: 30px;
    width: 40px;
    padding: 0;
    margin-left: 16px;

    &:after {
      clear: both;
    }
  }

}
:deep(.el-table) {
  .el-table__header-wrapper th.el-table__cell {
    height: 48px;
  }
  .el-table__row {
    height: 48px;
  }
  .el-table__cell {
    padding: 12px 16px; /* Vertical and horizontal padding */
    text-align: left !important;
  }
  .cell {
    text-align: left !important;
    display: flex !important;
    justify-content: flex-start !important; /* Forces layout items to align left */
  }
  --el-table-border-radius: 8px;
}
.chart-configuration {
  display: flex;
  margin-bottom: 24px;

  .members-filter {
    display: flex;
    align-items: center;
  }

  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
.sent-count-class {
  color: #088A08;
  font-weight: bold;
}

.failed-count-class {
  color: #F5276C;
  font-weight: bold;
}

.deleted-count-class {
  color: #9810FA;
  font-weight: bold;
}

.stopped-count-class {
  color: #FF0000;
  font-weight: bold;
}

.pending-count-class {
  color: #4671B3;
  font-weight: bold;
}


</style>
